import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { Button, ButtonLink } from '@/components/ui/Button';
import { OverflowMenu } from '@/components/ui/OverflowMenu';
import { formatDurationMs } from '@/lib/format';

import type { Transcription } from '../api';
import { groupMeetingsByMonth } from '../groupMeetingsByMonth';
import {
  formatClientLabel,
  formatMeetingDate,
  formatMeetingType,
  formatParticipants,
} from '../meetingLabels';
import { StatusBadge } from './StatusBadge';

export function TranscriptionsTable({
  items,
  currentUserId,
  isAdmin,
  deletingId = null,
  onDelete,
  onEdit,
  onViewResult,
}: {
  items: readonly Transcription[];
  currentUserId: string | null | undefined;
  isAdmin: boolean;
  deletingId?: string | null;
  onDelete?: (id: string, title: string) => void;
  onEdit?: (meeting: Transcription) => void;
  onViewResult?: (meeting: Transcription) => void;
}) {
  const groups = groupMeetingsByMonth(items);
  const colCount = 6;

  return (
    <div className="data-table-wrapper data-table-wrapper--journal">
      <table className="data-table data-table--journal">
        <caption className="visually-hidden">Журнал встреч</caption>
        <colgroup>
          <col className="data-table__col data-table__col--meeting" />
          <col className="data-table__col data-table__col--client" />
          <col className="data-table__col data-table__col--participants" />
          <col className="data-table__col data-table__col--status" />
          <col className="data-table__col data-table__col--duration" />
          <col className="data-table__col data-table__col--actions" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Встреча</th>
            <th scope="col">Клиент</th>
            <th scope="col">Участники</th>
            <th scope="col">Статус</th>
            <th scope="col">Длит.</th>
            <th scope="col">Действия</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={`month-${group.key}`}>
              <tr className="data-table__month-row">
                <th scope="colgroup" colSpan={colCount}>
                  {group.label}
                </th>
              </tr>
              {group.items.map((item) => {
                const axiumNames = item.axiumParticipants.map((p) => p.displayName);
                const title = item.title ?? `Без названия · ${item.id}`;
                const canEdit =
                  isAdmin ||
                  (currentUserId !== null &&
                    currentUserId !== undefined &&
                    item.createdById === currentUserId);
                const canDelete = isAdmin;
                const menuItems = [
                  ...(canEdit && onEdit
                    ? [
                        {
                          id: 'edit',
                          label: 'Редактировать',
                          onSelect: () => onEdit(item),
                        },
                      ]
                    : []),
                  ...(canDelete && onDelete
                    ? [
                        {
                          id: 'delete',
                          label: deletingId === item.id ? 'Удаление…' : 'Удалить',
                          danger: true,
                          disabled: deletingId === item.id,
                          onSelect: () => onDelete(item.id, title),
                        },
                      ]
                    : []),
                ];
                return (
                  <tr key={item.id}>
                    <td>
                      <Link
                        className="data-table__name"
                        to={`/transcriptions/${item.id}`}
                        title={title}
                      >
                        {title}
                      </Link>
                      <div className="data-table__sub">
                        {formatMeetingDate(item.meetingDate)} · {formatMeetingType(item.meetingType)}
                      </div>
                    </td>
                    <td className="data-table__muted">
                      {formatClientLabel(item.meetingType, item.clientName)}
                    </td>
                    <td className="data-table__muted">{formatParticipants(axiumNames)}</td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="data-table__muted data-table__duration">
                      {formatDurationMs(item.durationMs)}
                    </td>
                    <td>
                      <div className="data-table__actions">
                        {item.latestChatGptResult !== null ? (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => onViewResult?.(item)}
                          >
                            Посмотреть результат
                          </Button>
                        ) : (
                          <span className="data-table__muted data-table__result-empty">
                            Результат не добавлен
                          </span>
                        )}
                        <ButtonLink variant="secondary" size="sm" to={`/transcriptions/${item.id}`}>
                          Открыть встречу
                        </ButtonLink>
                        <OverflowMenu label={`Действия встречи «${title}»`} items={menuItems} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
